import {
  ArrowPathIcon,
  ClipboardDocumentIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline"
import { useCallback, useEffect } from "react"
import { toast } from "react-toastify"
import {
  type BudgetRecapRequest,
  useBudgetRecommenderControllerGenerateRecapMutation,
} from "~/ui-rtk/api/budgetRecommenderApi"
import { MarkdownContent } from "~/ui-rtk/components/chatbot/MarkdownContent"
import { Loader } from "~/ui-rtk/components/ui/common/Loader"
import { Button } from "~/ui-rtk/components/ui/controls"
import { copyTextToClipboard } from "~/ui-rtk/utils/clipboard"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"

interface RecapPanelProps {
  /** Memoised by the caller — a new object re-generates the recap. */
  request: BudgetRecapRequest
  /** Written from the review screen, before anything was sent. */
  preview?: boolean
}

/**
 * "The story of the budget update" (MD-5417): once an approve has landed,
 * the recap of what changed and why is written automatically and offered as
 * copyable markdown for the client update. Nothing is stored — Regenerate
 * asks for a fresh take on the same changes.
 */
export default function RecapPanel({ request, preview }: RecapPanelProps) {
  const [generate, { data, isLoading, isError, error }] =
    useBudgetRecommenderControllerGenerateRecapMutation()

  useEffect(() => {
    generate(request)
  }, [generate, request])

  const handleCopy = useCallback(async () => {
    if (!data) return
    try {
      await copyTextToClipboard(data.recap)
      toast.success("Recap copied to clipboard")
    } catch {
      toast.error("Couldn't copy the recap")
    }
  }, [data])

  return (
    <div className="rounded-lg border border-basic-blue bg-system-blue-800/40">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-basic-blue px-4 py-3">
        <div>
          <h3 className="flex items-center gap-1.5 text-sm font-medium text-gray-300">
            <SparklesIcon className="h-4 w-4 text-system-blue-300" />
            {preview ? "Client recap preview" : "Client recap"}
          </h3>
          <p className="text-xs text-gray-400">
            {preview
              ? "Written as if the changes under review were applied — nothing has been sent yet."
              : "The story behind these changes — what moved, why, what's working and what we're fixing — ready to paste into the client update."}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant={{ variant: "outlined" }}
            size="sm"
            type="button"
            className="flex whitespace-nowrap"
            disabled={isLoading}
            onClick={() => generate(request)}
          >
            <ArrowPathIcon className="h-4 w-4" />
            Regenerate
          </Button>
          <Button
            variant={{ variant: "solid", color: "blue" }}
            size="sm"
            type="button"
            className="flex whitespace-nowrap"
            disabled={!data || isLoading}
            onClick={handleCopy}
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
            Copy recap
          </Button>
        </div>
      </div>

      <div className="px-4 py-3">
        {isLoading && (
          <div className="flex items-center gap-3 py-4 text-sm text-gray-400">
            <Loader size="small" />
            Writing the recap…
          </div>
        )}
        {!isLoading && isError && (
          <p className="text-sm text-rose-300">
            Couldn&apos;t write the recap:{" "}
            {getServerErrorMessage(error, "the request failed")}.{" "}
            {preview
              ? "Nothing was sent."
              : "The budget changes above still stand."}{" "}
            Try Regenerate.
          </p>
        )}
        {!isLoading && data && (
          <>
            {data.adsUnavailable && (
              <p className="mb-2 text-xs text-amber-300">
                Ad-level detail was unavailable, so the recap was written
                without the top ads.
              </p>
            )}
            <div className="text-sm [&_.prose]:text-sm [&_.prose_h2]:mb-1 [&_.prose_h2]:mt-3 [&_.prose_h2]:text-base [&_.prose_ul]:my-1">
              <MarkdownContent>{data.recap}</MarkdownContent>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
